import React, { useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Sparkles, FileText, ChevronRight, Loader2, Zap } from 'lucide-react';
import { generateOutline, generateContent, confirmGenerateImages } from '../../services/contentService';
import { parseOutlinePages } from '../../pages/ContentHistory/historyUtils';

/* ══════════════════════════════════════════════════════════
   ContentWizardModal — 内容创作向导（两步）
   步骤 1: 配置（主题 + 页数）
   步骤 2: 大纲编辑（AI 生成 + 逐页可编辑）
   提交后创建异步任务 → 跳转历史页
   ══════════════════════════════════════════════════════════ */

const PAGE_OPTIONS = [1, 3, 5, 7, 9];

const StepIndicator = ({ step }) => (
    <div className="flex items-center gap-3">
        {/* Step 1 */}
        <div className="flex items-center gap-2">
            <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-all ${step >= 1 ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'bg-gray-200 text-gray-500'
                }`}>1</div>
            <span className={`text-sm font-medium ${step >= 1 ? 'text-gray-900' : 'text-gray-400'}`}>配置</span>
        </div>
        {/* Line */}
        <div className={`h-0.5 w-8 rounded-full transition-all ${step >= 2 ? 'bg-blue-500' : 'bg-gray-200'}`} />
        {/* Step 2 */}
        <div className="flex items-center gap-2">
            <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-all ${step >= 2 ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'bg-gray-200 text-gray-500'
                }`}>2</div>
            <span className={`text-sm font-medium ${step >= 2 ? 'text-gray-900' : 'text-gray-400'}`}>大纲编辑</span>
        </div>
    </div>
);

const ContentWizardModal = ({ open, onClose, hotspot, token, onComplete }) => {
    const [step, setStep] = useState(1);
    const [topic, setTopic] = useState(hotspot?.topic || hotspot?.title || '');
    const [skillName, setSkillName] = useState(hotspot?.style || 'xiaohongshu_default');
    const [pageCount, setPageCount] = useState(5);
    const [pages, setPages] = useState([]);
    const [taskId, setTaskId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const autoGenerateRef = useRef(false);

    // Reset state when hotspot changes
    React.useEffect(() => {
        if (open && hotspot) {
            const t = hotspot?.topic || hotspot?.title || '';
            const pc = hotspot?.pageCount || 5;
            setTopic(t);
            setSkillName(hotspot?.style || 'xiaohongshu_default');
            setPageCount(pc);
            setPages([]);
            setTaskId('');
            setError('');
            setSubmitting(false);

            // 从历史页打开 draft 任务（已有 outline + task_id）
            if (hotspot?.outline && hotspot?.task_id) {
                setTaskId(hotspot.task_id);
                // 解析已有大纲为页面
                const parsed = parseOutlinePages(hotspot.outline);
                setPages(parsed.length > 0 ? parsed : [{ type: '封面', content: '', order: 1 }]);
                setStep(2);
                setLoading(false);
            }
            // 从创作页进入时（已带 pageCount），直接进入步骤2并自动生成
            else if (hotspot?.pageCount && t) {
                setStep(2);
                setLoading(true);
                autoGenerateRef.current = true;
            } else {
                setStep(1);
                setLoading(false);
            }
        }
    }, [open, hotspot]);

    const handleGenerateOutline = useCallback(async () => {
        if (!topic.trim() || loading) return;
        setLoading(true);
        setError('');
        try {
            const result = await generateOutline(topic, skillName, token, pageCount);
            const outlinePages = result?.outline?.pages || [];
            // Adjust to requested page count
            let adjusted = outlinePages.slice(0, pageCount);
            while (adjusted.length < pageCount) {
                const idx = adjusted.length + 1;
                const type = idx === 1 ? '封面' : idx === pageCount ? '总结' : '内容';
                adjusted.push({
                    type,
                    content: '',
                    order: idx,
                });
            }
            setPages(adjusted);
            setTaskId(result?.task_id || '');
            setStep(2);
        } catch (err) {
            setError(err.message || '大纲生成失败，请稍后重试');
        } finally {
            setLoading(false);
        }
    }, [topic, pageCount, token, loading, skillName]);

    // 自动触发大纲生成（从创作页进入时，跳过步骤1）
    // 注意：reset effect 中 setState 在同一渲染周期内尚未生效，这里优先使用 hotspot 的即时配置
    React.useEffect(() => {
        if (autoGenerateRef.current && open && (hotspot?.topic || hotspot?.title || topic)) {
            autoGenerateRef.current = false;
            const effectiveTopic = hotspot?.topic || hotspot?.title || topic;
            const effectiveSkillName = hotspot?.style || skillName || 'xiaohongshu_default';
            const effectivePageCount = hotspot?.pageCount || pageCount;

            // 直接调 API，不经过 handleGenerateOutline 的 loading 检查
            (async () => {
                try {
                    const result = await generateOutline(effectiveTopic, effectiveSkillName, token, effectivePageCount);
                    const outlinePages = result?.outline?.pages || [];
                    let adjusted = outlinePages.slice(0, effectivePageCount);
                    while (adjusted.length < effectivePageCount) {
                        const idx = adjusted.length + 1;
                        const type = idx === 1 ? '封面' : idx === effectivePageCount ? '总结' : '内容';
                        adjusted.push({ type, content: '', order: idx });
                    }
                    setPages(adjusted);
                    setTaskId(result?.task_id || '');
                } catch (err) {
                    setError(err.message || '大纲生成失败，请稍后重试');
                } finally {
                    setLoading(false);
                }
            })();
        }
    }, [open, hotspot, topic, skillName, token, pageCount]);

    const handlePageContentChange = useCallback((index, newContent) => {
        setPages(prev => prev.map((p, i) => i === index ? { ...p, content: newContent } : p));
    }, []);

    const handleSubmit = useCallback(async () => {
        if (submitting || loading || !taskId) return;
        setSubmitting(true);
        setError('');
        try {
            // 拼接编辑后的大纲文本
            const outlineText = pages.map(p => `<page>${p.content}</page>`).join('\n');
            // 1. 先生成文案（titles/copywriting/tags）
            await generateContent(taskId, topic, outlineText, token);
            // 2. 再生成图片
            await confirmGenerateImages(taskId, outlineText, token);
            onComplete?.();
        } catch (err) {
            setError(err.message || '生成失败，请稍后重试');
        } finally {
            setSubmitting(false);
        }
    }, [pages, taskId, topic, token, submitting, loading, onComplete]);

    if (!open) return null;

    const estimatedCost = 1 + 6 * pageCount;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-8">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={onClose} />

            {/* Modal */}
            <div className="relative flex w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl animate-fade-in"
                style={{ maxHeight: '88vh' }}
            >
                {/* Header */}
                <div className="flex shrink-0 items-center justify-between border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white px-6 py-4">
                    <div className="flex items-center gap-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-blue-500 shadow-lg shadow-blue-200">
                            <Sparkles className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">内容创作向导</h3>
                            <StepIndicator step={step} />
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Error */}
                    {error && (
                        <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                            {error}
                        </div>
                    )}

                    {step === 1 && (
                        <div className="space-y-6">
                            {/* Topic */}
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-gray-700">主题</label>
                                <textarea
                                    value={topic}
                                    onChange={e => setTopic(e.target.value)}
                                    rows={3}
                                    className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800 outline-none transition-all placeholder:text-gray-400 focus:border-blue-300 focus:bg-white focus:ring-2 focus:ring-blue-100"
                                    placeholder="输入或编辑创作主题…"
                                />
                            </div>

                            {/* Page Count */}
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-gray-700">页数</label>
                                <p className="mb-3 text-xs text-gray-400">选择小红书图片页数，每页将生成一段大纲和对应图片</p>
                                <div className="flex gap-2">
                                    {PAGE_OPTIONS.map(n => (
                                        <button
                                            key={n}
                                            type="button"
                                            onClick={() => setPageCount(n)}
                                            className={`flex h-12 w-12 items-center justify-center rounded-2xl text-sm font-bold transition-all ${pageCount === n
                                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 scale-105'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                        >
                                            {n}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Cost estimate */}
                            <div className="flex items-center gap-2 rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3">
                                <Zap className="h-4 w-4 text-amber-500" />
                                <span className="text-sm text-amber-700">
                                    预估消耗 <strong>{estimatedCost}</strong> 积分（大纲 1 + 图片 {6 * pageCount}）
                                </span>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4">
                            {loading ? (
                                /* 大纲生成中 — 骨架屏 */
                                <>
                                    <div className="flex items-center gap-2 text-sm text-blue-600">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        <span>AI 正在生成大纲，请稍候…</span>
                                    </div>
                                    {Array.from({ length: pageCount }).map((_, i) => (
                                        <div key={i} className="animate-pulse rounded-2xl border border-gray-100 bg-gray-50 p-4">
                                            <div className="mb-3 h-5 w-16 rounded-lg bg-gray-200" />
                                            <div className="space-y-2">
                                                <div className="h-3 w-full rounded bg-gray-200" />
                                                <div className="h-3 w-4/5 rounded bg-gray-200" />
                                                <div className="h-3 w-3/5 rounded bg-gray-200" />
                                            </div>
                                        </div>
                                    ))}
                                </>
                            ) : (
                                /* 大纲已生成 — 可编辑 */
                                <>
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <FileText className="h-4 w-4" />
                                        <span>AI 已生成 {pages.length} 页大纲，你可以逐页编辑后提交</span>
                                    </div>

                                    {pages.map((page, idx) => (
                                        <div key={idx} className="group rounded-2xl border border-gray-100 bg-gray-50 p-4 transition-all hover:border-blue-200 hover:bg-blue-50/30">
                                            <div className="mb-2 flex items-center gap-2">
                                                <span className={`inline-flex items-center rounded-lg px-2.5 py-0.5 text-xs font-bold ${idx === 0
                                                    ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white'
                                                    : 'bg-gray-200 text-gray-600'
                                                    }`}>
                                                    {idx === 0 ? '封面' : `第${idx + 1}页`}
                                                </span>
                                            </div>
                                            <textarea
                                                value={page.content}
                                                onChange={e => handlePageContentChange(idx, e.target.value)}
                                                rows={3}
                                                className="w-full resize-none rounded-xl border-0 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none transition-all placeholder:text-gray-300 focus:ring-2 focus:ring-blue-100"
                                                placeholder={`第${idx + 1}页的大纲内容…`}
                                            />
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex shrink-0 items-center justify-between border-t border-gray-100 bg-gray-50/50 px-6 py-4">
                    <div></div>

                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-xl px-4 py-2.5 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100"
                        >
                            取消
                        </button>

                        {step === 1 && (
                            <button
                                type="button"
                                onClick={handleGenerateOutline}
                                disabled={!topic.trim() || loading}
                                className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white shadow-lg transition-all ${!topic.trim() || loading
                                    ? 'cursor-not-allowed bg-gray-300 shadow-none'
                                    : 'bg-gradient-to-r from-blue-600 to-blue-500 shadow-blue-200 hover:shadow-xl hover:-translate-y-0.5'
                                    }`}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        生成大纲中…
                                    </>
                                ) : (
                                    <>
                                        生成大纲
                                        <ChevronRight className="h-4 w-4" />
                                    </>
                                )}
                            </button>
                        )}

                        {step === 2 && (
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={submitting || loading || !taskId}
                                className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white shadow-lg transition-all ${submitting || loading || !taskId
                                    ? 'cursor-not-allowed bg-gray-300 shadow-none'
                                    : 'bg-gradient-to-r from-emerald-600 to-emerald-500 shadow-emerald-200 hover:shadow-xl hover:-translate-y-0.5'
                                    }`}
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        提交中…
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="h-4 w-4" />
                                        提交生成
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ContentWizardModal;
